import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Target, PhoneMissed, HelpCircle, AlertTriangle, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { contactCenterAPI } from '../lib/api';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, Legend,
} from 'recharts';

const RANGE_OPTIONS = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
];

const OUTCOME_COLORS = {
  booked: '#22c55e',
  estimate_scheduled: '#3b82f6',
  follow_up_needed: '#f59e0b',
  left_voicemail: '#8b5cf6',
  no_answer: '#ef4444',
  spam: '#6b7280',
  other: '#94a3b8',
  untagged: '#d1d5db',
};

const OUTCOME_LABELS = {
  booked: 'Booked',
  estimate_scheduled: 'Estimate Scheduled',
  follow_up_needed: 'Follow Up Needed',
  left_voicemail: 'Left Voicemail',
  no_answer: 'No Answer',
  spam: 'Spam',
  other: 'Other',
  untagged: 'No Outcome',
};

function StatCard({ label, value, sub, icon: Icon, color }) {
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold mt-1 tabular-nums">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          {Icon && (
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color || 'bg-muted'}`}>
              <Icon className="w-4.5 h-4.5 text-white" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function InsightBlock({ data }) {
  const insights = [];

  if (data.no_outcome_count > 0) {
    insights.push({
      type: 'warning',
      text: `${data.no_outcome_count} call${data.no_outcome_count > 1 ? 's are' : ' is'} missing outcomes — tag them to avoid lost revenue.`,
    });
  }

  if (data.missed_rate > 25) {
    insights.push({
      type: 'alert',
      text: `High missed call rate (${data.missed_rate}%) — consider faster response or enabling auto-text.`,
    });
  }

  if (data.booking_rate > 0 && data.booking_rate < 15) {
    insights.push({
      type: 'info',
      text: `Booking rate is ${data.booking_rate}% — review follow-up process for unconverted leads.`,
    });
  }

  if (insights.length === 0 && data.total_calls > 0) {
    insights.push({
      type: 'success',
      text: 'Call metrics look healthy. Keep tagging outcomes to track conversion over time.',
    });
  }

  if (data.total_calls === 0) return null;

  const iconMap = { warning: AlertTriangle, alert: PhoneMissed, info: TrendingUp, success: Target };
  const colorMap = {
    warning: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/20 dark:border-amber-800 dark:text-amber-300',
    alert: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950/20 dark:border-red-800 dark:text-red-300',
    info: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950/20 dark:border-blue-800 dark:text-blue-300',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/20 dark:border-emerald-800 dark:text-emerald-300',
  };

  return (
    <div className="space-y-2" data-testid="insights-section">
      {insights.map((ins, i) => {
        const Icon = iconMap[ins.type];
        return (
          <div key={i} className={`flex items-start gap-2.5 px-3.5 py-2.5 rounded-md border text-sm ${colorMap[ins.type]}`}>
            <Icon className="w-4 h-4 mt-0.5 shrink-0" />
            <p>{ins.text}</p>
          </div>
        );
      })}
    </div>
  );
}

export default function ContactCenterAnalytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeDays, setRangeDays] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const from = new Date(now);
      from.setDate(from.getDate() - rangeDays);
      const res = await contactCenterAPI.analytics({
        date_from: from.toISOString(),
        date_to: now.toISOString(),
      });
      setData(res.data);
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [rangeDays]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Prepare outcome chart data
  const outcomeChartData = data ? Object.entries(data.outcome_breakdown)
    .map(([key, count]) => ({
      name: OUTCOME_LABELS[key] || key,
      value: count,
      color: OUTCOME_COLORS[key] || '#94a3b8',
    }))
    .sort((a, b) => b.value - a.value) : [];

  // Prepare trend data with formatted dates
  const trendData = data ? data.daily_trends.map(d => ({
    ...d,
    label: new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  })) : [];

  return (
    <Layout>
      <div className="px-6 py-6 space-y-6" data-testid="analytics-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/calls')} data-testid="back-to-inbox">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-lg font-bold">Call Analytics</h1>
              <p className="text-xs text-muted-foreground">Last {rangeDays} days</p>
            </div>
          </div>
          <div className="flex gap-1" data-testid="range-filters">
            {RANGE_OPTIONS.map(opt => (
              <Button
                key={opt.days}
                variant={rangeDays === opt.days ? 'default' : 'outline'}
                size="sm"
                className="text-xs h-7"
                onClick={() => setRangeDays(opt.days)}
                data-testid={`range-${opt.days}`}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : !data || data.total_calls === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Phone className="h-10 w-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No call data for this period</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Calls will appear here once logged through the Contact Center</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Section 1 — Top Stat Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stat-cards">
              <StatCard label="Total Calls" value={data.total_calls} icon={Phone} color="bg-blue-500" />
              <StatCard label="Booking Rate" value={`${data.booking_rate}%`} sub={`${data.booked_count} booked`} icon={Target} color="bg-emerald-500" />
              <StatCard label="Missed Rate" value={`${data.missed_rate}%`} sub={`${data.missed_count} missed`} icon={PhoneMissed} color="bg-red-500" />
              <StatCard label="No Outcome" value={data.no_outcome_count} sub="calls untagged" icon={HelpCircle} color="bg-gray-400" />
            </div>

            {/* Section 4 — Actionable Insights */}
            <InsightBlock data={data} />

            {/* Section 2 + 3 — Charts side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Outcome Breakdown */}
              <Card data-testid="outcome-chart">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold mb-4">Outcome Breakdown</h3>
                  {outcomeChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={outcomeChartData} layout="vertical" margin={{ left: 10, right: 16, top: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border)" opacity={0.4} />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid var(--border)' }}
                          formatter={(val) => [val, 'Calls']}
                        />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={24}>
                          {outcomeChartData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-muted-foreground py-8 text-center">No outcome data</p>
                  )}
                </CardContent>
              </Card>

              {/* Daily Trends */}
              <Card data-testid="trends-chart">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold mb-4">Daily Trends</h3>
                  {trendData.length > 1 ? (
                    <ResponsiveContainer width="100%" height={320}>
                      <LineChart data={trendData} margin={{ left: 0, right: 16, top: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4} />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid var(--border)' }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="total_calls" name="Total Calls" stroke="#3b82f6" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="booked_calls" name="Booked" stroke="#22c55e" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : trendData.length === 1 ? (
                    <div className="text-center py-8">
                      <p className="text-sm text-muted-foreground">Only 1 day of data — trends require at least 2 days</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        {trendData[0].label}: {trendData[0].total_calls} calls, {trendData[0].booked_calls} booked
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground py-8 text-center">No trend data</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
